import { oc } from "@orpc/contract";
import { z } from "zod";
import { TASK_STATUSES } from "@erp/shared-types";

// --- Enums ---

// Built from the canonical status list in @erp/shared-types so the runtime
// validator and the transition state machine (in @erp/shared-utils) can
// never drift apart.
export const TaskStatusSchema = z.enum(TASK_STATUSES);

export const TaskPrioritySchema = z.enum(["low", "medium", "high", "urgent"]);

export const TaskAssigneeTypeSchema = z.enum(["user", "agent"]);

// --- Response schema ---

export const TaskResponseSchema = z.object({
  id: z.uuid(),
  organizationId: z.uuid(),
  // The namespace the task belongs to, its per-namespace number, and the
  // derived human-readable id (e.g. "TASK-42"). Nullable for tasks not yet
  // numbered (pre-backfill).
  taskNamespaceId: z.uuid().nullable(),
  taskNumber: z.number().int().nullable(),
  displayId: z.string().nullable(),
  title: z.string(),
  description: z.string().nullable(),
  status: TaskStatusSchema,
  priority: TaskPrioritySchema,
  assigneeType: TaskAssigneeTypeSchema.nullable(),
  assigneeUserId: z.uuid().nullable(),
  assigneeAgentDefinitionId: z.uuid().nullable(),
  createdByUserId: z.uuid().nullable(),
  dueDate: z.coerce.date().nullable(),
  startedAt: z.coerce.date().nullable(),
  completedAt: z.coerce.date().nullable(),
  metadata: z.record(z.string(), z.unknown()).nullable(),
  tags: z.array(z.string()).nullable(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  deletedAt: z.coerce.date().nullable().optional(),
  // Populated by list/get to indicate the calling user watches this task.
  // Optional so existing handlers that don't set it remain valid.
  isWatching: z.boolean().optional(),
});

// --- Input schemas ---

export const CreateTaskInputSchema = z.object({
  title: z.string().min(1).max(500),
  description: z.string().max(10000).optional(),
  priority: TaskPrioritySchema.optional(),
  assigneeType: TaskAssigneeTypeSchema.optional(),
  assigneeUserId: z.uuid().optional(),
  assigneeAgentDefinitionId: z.uuid().optional(),
  dueDate: z.coerce.date().optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
  tags: z.array(z.string()).optional(),
  // Omit to place the task in the organization's default namespace.
  taskNamespaceId: z.uuid().optional(),
});

export const UpdateTaskInputSchema = z.object({
  id: z.uuid(),
  title: z.string().min(1).max(500).optional(),
  description: z.string().max(10000).nullable().optional(),
  priority: TaskPrioritySchema.optional(),
  dueDate: z.coerce.date().nullable().optional(),
  metadata: z.record(z.string(), z.unknown()).nullable().optional(),
  tags: z.array(z.string()).nullable().optional(),
  // Move the task to another namespace. The task is re-numbered from the
  // destination's sequence; its old number is abandoned (a permanent gap).
  taskNamespaceId: z.uuid().optional(),
});

export const ListTasksInputSchema = z.object({
  limit: z.int().min(1).max(100).prefault(20),
  offset: z.int().min(0).prefault(0),
  // Filters
  status: TaskStatusSchema.optional(),
  statuses: z.array(TaskStatusSchema).optional(),
  priority: TaskPrioritySchema.optional(),
  assigneeType: TaskAssigneeTypeSchema.optional(),
  assigneeUserId: z.uuid().optional(),
  assigneeAgentDefinitionId: z.uuid().optional(),
  // With assigneeAgentDefinitionId: also match tasks escalated away from that
  // agent (a needs_input hand-off reassigns the task to a user, keeping the
  // original agent only in metadata.escalatedFromAgentDefinitionId).
  includeEscalatedFromAgent: z.boolean().optional(),
  createdByUserId: z.uuid().optional(),
  watchedByUserId: z.uuid().optional(),
  taskNamespaceId: z.uuid().optional(),
  dueBefore: z.coerce.date().optional(),
  dueAfter: z.coerce.date().optional(),
  // Activity-axis date ranges. Unlike dueDate (often null), createdAt/updatedAt
  // are always set, so a range on them is the useful default for an inbox.
  createdAfter: z.coerce.date().optional(),
  createdBefore: z.coerce.date().optional(),
  updatedAfter: z.coerce.date().optional(),
  updatedBefore: z.coerce.date().optional(),
  search: z.string().optional(),
  // Sort
  sortBy: z
    .enum(["createdAt", "updatedAt", "dueDate", "priority", "status", "title"])
    .optional(),
  sortOrder: z.enum(["asc", "desc"]).optional(),
});

export const ListTasksResponseSchema = z.object({
  tasks: z.array(TaskResponseSchema),
  pagination: z.object({
    total: z.number(),
    limit: z.number(),
    offset: z.number(),
    hasMore: z.boolean(),
  }),
});

export const AssignTaskInputSchema = z.object({
  id: z.uuid(),
  assigneeType: TaskAssigneeTypeSchema,
  assigneeUserId: z.uuid().optional(),
  assigneeAgentDefinitionId: z.uuid().optional(),
});

export const TransitionTaskInputSchema = z.object({
  id: z.uuid(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const BatchGetTasksInputSchema = z.object({
  assigneeAgentDefinitionId: z.uuid(),
  statuses: z.array(TaskStatusSchema).optional(),
  limit: z.int().min(1).max(100).prefault(20),
});

export const BatchUpdateTasksInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(100),
  status: TaskStatusSchema.optional(),
  metadata: z.record(z.string(), z.unknown()).optional(),
});

export const BatchReturnToAgentInputSchema = z.object({
  taskIds: z.array(z.uuid()).min(1).max(100),
});

/**
 * A meaningful agent action anchored to the recording timeline. Drives the
 * task video player's timeline markers, click overlay, and skip-inactivity.
 * Screenshots are intentionally excluded — the task surface only needs markers.
 */
export const TaskRecordingActionSchema = z.object({
  id: z.uuid(),
  startedAt: z.iso.datetime(),
  actionType: z.string().nullable(),
  actionText: z.string().nullable(),
  coordinate: z.tuple([z.number(), z.number()]).nullable(),
  viewportOffset: z.tuple([z.number(), z.number()]).nullable(),
});

export const TaskRecordingResponseSchema = z.object({
  recordingDocumentId: z.string(),
  signedUrl: z.string(),
  agentRunCompletedAt: z.coerce.date().nullable(),
  actions: z.array(TaskRecordingActionSchema),
});

// --- Contracts ---

export const tasksContracts = {
  create: oc
    .input(CreateTaskInputSchema)
    .output(TaskResponseSchema)
    .route({ method: "POST" }),

  get: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskResponseSchema.nullable())
    .route({ method: "GET" }),

  list: oc
    .input(ListTasksInputSchema)
    .output(ListTasksResponseSchema)
    .route({ method: "GET" }),

  update: oc
    .input(UpdateTaskInputSchema)
    .output(TaskResponseSchema)
    .route({ method: "PUT" }),

  delete: oc
    .input(z.object({ id: z.uuid() }))
    .output(z.object({ success: z.boolean() }))
    .route({ method: "DELETE" }),

  assign: oc
    .input(AssignTaskInputSchema)
    .output(TaskResponseSchema)
    .route({ method: "PUT" }),

  unassign: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskResponseSchema)
    .route({ method: "PUT" }),

  start: oc
    .input(TransitionTaskInputSchema)
    .output(TaskResponseSchema)
    .route({ method: "POST" }),

  complete: oc
    .input(TransitionTaskInputSchema)
    .output(TaskResponseSchema)
    .route({ method: "POST" }),

  fail: oc
    .input(TransitionTaskInputSchema)
    .output(TaskResponseSchema)
    .route({ method: "POST" }),

  cancel: oc
    .input(TransitionTaskInputSchema)
    .output(TaskResponseSchema)
    .route({ method: "POST" }),

  batchGet: oc
    .input(BatchGetTasksInputSchema)
    .output(z.array(TaskResponseSchema))
    .route({ method: "POST" }),

  batchUpdate: oc
    .input(BatchUpdateTasksInputSchema)
    .output(z.array(TaskResponseSchema))
    .route({ method: "PUT" }),

  // Re-assign each task back to its originating agent and re-queue it (pending).
  // The target agent is resolved per-task on the server, so a single bulk call
  // can return many tasks each to its own agent.
  batchReturnToAgent: oc
    .input(BatchReturnToAgentInputSchema)
    .output(z.array(TaskResponseSchema))
    .route({ method: "PUT" }),

  getRecording: oc
    .input(z.object({ id: z.uuid() }))
    .output(TaskRecordingResponseSchema.nullable())
    .route({ method: "GET" }),
} as const;

// --- Type exports ---

export type TaskStatus = z.infer<typeof TaskStatusSchema>;
export type TaskPriority = z.infer<typeof TaskPrioritySchema>;
export type TaskAssigneeType = z.infer<typeof TaskAssigneeTypeSchema>;
export type TaskResponse = z.infer<typeof TaskResponseSchema>;
export type CreateTaskInput = z.infer<typeof CreateTaskInputSchema>;
export type UpdateTaskInput = z.infer<typeof UpdateTaskInputSchema>;
export type ListTasksInput = z.infer<typeof ListTasksInputSchema>;
export type ListTasksResponse = z.infer<typeof ListTasksResponseSchema>;
export type AssignTaskInput = z.infer<typeof AssignTaskInputSchema>;
export type TransitionTaskInput = z.infer<typeof TransitionTaskInputSchema>;
export type BatchGetTasksInput = z.infer<typeof BatchGetTasksInputSchema>;
export type BatchUpdateTasksInput = z.infer<typeof BatchUpdateTasksInputSchema>;
export type BatchReturnToAgentInput = z.infer<
  typeof BatchReturnToAgentInputSchema
>;
export type TaskRecordingResponse = z.infer<typeof TaskRecordingResponseSchema>;
export type TaskRecordingAction = z.infer<typeof TaskRecordingActionSchema>;
